// The dashboard's headline tiles: total responses and completion rate
// always; average score only when the survey has a scored step. That score
// is the raw mean on the survey's own scale — not the overview's normalized
// percentage — so the tile says which scale: "3.2 / 4" when the scored
// step's option values pin the top, in words when they don't. Hence the
// `survey` prop alongside `stats`. "Vs previous period" is always there too:
// the change in responses, plus the change in average score when scored —
// or "No earlier data" when there's no previous window to compare with
// (including All time, which has no "previous" by definition). A survey
// with a link placement adds "Response rate": answered over invited per
// placement, the one number a mailed survey has that a kiosk does not.

import type { Survey, SurveyStats } from "@/api/types";
import { DeltaLine, NoEarlierData, StatTile, TILE_VALUE } from "./stat-tile";
import { completionRate, countDeltaVsPrevious, deltaVsPrevious, metricScaleMax } from "./stats-mapping";

export interface SummaryTilesProps {
    stats: SurveyStats;
    survey: Survey;
    /** All time has no earlier window to compare with. */
    allTime?: boolean;
}

function formatScore(score: number | null): string {
    return score === null ? "—" : score.toFixed(1);
}

/** "4" for a whole-number top, "4.5" otherwise — never "4.0" next to "3.2". */
function formatScaleMax(max: number): string {
    return Number.isInteger(max) ? String(max) : max.toFixed(1);
}

function formatPercent(rate: number | null): string {
    return rate === null ? "—" : `${Math.round(rate * 100)}%`;
}

export function SummaryTiles({ stats, survey, allTime }: SummaryTilesProps) {
    const completion = completionRate(stats);
    const delta = deltaVsPrevious(stats);
    const countDelta = allTime ? null : countDeltaVsPrevious(stats);
    const hasScore = stats.metricStepId !== null;
    const scaleMax = metricScaleMax(survey);
    const invites = stats.invites ?? [];

    return (
        <div className={`grid grid-cols-2 gap-4 ${hasScore ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
            <StatTile label="Responses">
                <p data-testid="tile-responses" className={TILE_VALUE}>
                    {stats.count}
                </p>
            </StatTile>
            {hasScore && (
                <StatTile label="Average score" hint={scaleMax === null ? "On this survey's scale" : undefined}>
                    <p data-testid="tile-average-score" className={TILE_VALUE}>
                        {formatScore(stats.averageScore)}
                        {stats.averageScore !== null && scaleMax !== null && (
                            <span className="text-base font-normal text-muted-foreground">
                                {` / ${formatScaleMax(scaleMax)}`}
                            </span>
                        )}
                    </p>
                </StatTile>
            )}
            <StatTile label="Completion">
                <p data-testid="tile-completion" className={TILE_VALUE}>
                    {formatPercent(completion)}
                </p>
            </StatTile>
            <StatTile label="Vs previous period">
                {countDelta === null ? (
                    <NoEarlierData />
                ) : (
                    <>
                        <DeltaLine testId="tile-delta-count" delta={countDelta} unit="responses" digits={0} />
                        {hasScore && delta !== null && (
                            <DeltaLine testId="tile-delta" delta={delta} unit="score" digits={1} />
                        )}
                    </>
                )}
            </StatTile>
            {invites.length > 0 && (
                <StatTile label="Response rate" hint="Answered invites, of those sent in this period">
                    <ul data-testid="tile-response-rate" className="flex flex-col gap-1">
                        {invites.map((placement) => (
                            <li key={placement.placementId} className="flex items-baseline justify-between gap-3">
                                <span className="truncate text-sm text-muted-foreground">
                                    {placement.placementName}
                                </span>
                                <span className={TILE_VALUE}>
                                    {formatPercent(
                                        placement.invited === 0 ? null : placement.answered / placement.invited,
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </StatTile>
            )}
        </div>
    );
}
